import { apiRequest, setRequestRecovery } from '@lib/api';
import { apiUrl } from '@lib/env';
import { z } from 'zod';

import { retryAfterRefresh, singleFlight } from '../model/refresh-policy';

/**
 * The employee-pool session (INI-0313, BO-1), the second sign-in security
 * screens need on top of Atelier's.
 *
 * Its tokens live in `HttpOnly` cookies the BFF set; this bundle never sees
 * them. What it can read is `GET /auth/employee/session`: which permissions
 * the session carries (the nav's source) and whether an expired one can be
 * refreshed. The server decides everything; this is UX.
 */
export const employeeSessionSchema = z.object({
    permissions: z.array(z.string()),
    expiresAt: z.string().nullable(),
    refreshable: z.boolean().default(false),
});

export type EmployeeSession = z.infer<typeof employeeSessionSchema>;

export function fetchEmployeeSession(signal?: AbortSignal): Promise<EmployeeSession> {
    return apiRequest('/auth/employee/session', employeeSessionSchema, { signal });
}

const refreshedSchema = employeeSessionSchema.omit({ refreshable: true });

const REFRESH_TIMEOUT_MS = 10_000;

/**
 * SHA-256 of the empty body. The refresh is a bodyless POST, and CloudFront's
 * OAC still needs the client to state the payload hash for a POST (see
 * `payloadHash` in `@lib/api`).
 */
const EMPTY_PAYLOAD_SHA256 = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855';

/**
 * `POST /auth/employee/refresh`: the BFF trades the refresh cookie for a new
 * access cookie. `null` when the server refused (401: no refresh cookie, or
 * Cognito revoked it, and both cookies are now gone). Any other failure throws,
 * because the cookies are untouched and a later try may work.
 *
 * Its own `fetch`, not `apiRequest`: every `apiRequest` goes through the
 * recovery below, and a refresh that recovered itself would wait on its own
 * in-flight promise forever.
 */
export async function postEmployeeRefresh(): Promise<EmployeeSession | null> {
    const response = await fetch(apiUrl('/auth/employee/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'x-amz-content-sha256': EMPTY_PAYLOAD_SHA256 },
        signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    });
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(`Employee session refresh failed with ${response.status}`);
    return { ...refreshedSchema.parse(await response.json()), refreshable: false };
}

/** Every caller shares one refresh in flight. */
export const refreshEmployeeSession = singleFlight(postEmployeeRefresh);

/**
 * Make every API request refresh the employee session once and retry once
 * when it answers `EMPLOYEE_SESSION_REQUIRED`. `onRefreshed` hands the new
 * session to whoever caches it, so the nav follows without another read.
 * Installed once, at the app root.
 */
export function installEmployeeSessionRecovery(
    onRefreshed: (session: EmployeeSession) => void,
): void {
    const refresh = async () => {
        const session = await refreshEmployeeSession();
        if (session) onRefreshed(session);
        return session !== null;
    };
    setRequestRecovery((attempt) => retryAfterRefresh(attempt, refresh));
}

/**
 * Where "Sign in with your Veevart Google account" goes. `returnTo` is the
 * current path; the BFF accepts only the viewer's path and falls back to it.
 */
export function employeeSignInUrl(returnTo: string): string {
    return `${apiUrl('/auth/employee/start')}?returnTo=${encodeURIComponent(returnTo)}`;
}
